
import { useState, useEffect } from "react";
import { File } from "lucide-react";
import { useDropzone } from 'react-dropzone';
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ExpenseFileUploadFieldProps {
  onFileChange: (file: File | null) => void;
  isUploading?: boolean;
}

export const ExpenseFileUploadField = ({ onFileChange, isUploading = false }: ExpenseFileUploadFieldProps) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles?.[0]) {
        const file = acceptedFiles[0];
        onFileChange(file);
        setFileName(file.name);
      }
    }
  });

  // Clean up the preview URL when component unmounts
  useEffect(() => {
    return () => {
      if (preview) {
        URL.revokeObjectURL(preview);
      }
    };
  }, [preview]);

  return (
    <div>
      <Label htmlFor="receipt" className="text-base mb-2 block">Comprobante (PDF)</Label>
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
          isDragActive ? 'border-primary bg-primary/10' : 'border-gray-300'
        }`}
      >
        <input {...getInputProps()} id="receipt" />
        <div className="text-center">
          {isUploading ? (
            <div className="flex flex-col items-center space-y-2">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="text-sm text-gray-500">Subiendo archivo...</p>
            </div>
          ) : fileName ? (
            <div className="flex flex-col items-center space-y-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center space-x-2">
                      <File className="h-8 w-8 text-blue-500" />
                      <p className="text-sm text-gray-700 truncate max-w-[200px]">{fileName}</p>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{fileName}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <p className="text-xs text-gray-500">
                Click o arrastra para cambiar el archivo
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <File className="h-8 w-8 text-gray-400" />
              <p className="text-sm text-gray-500">
                Click o arrastra un PDF aquí
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
